import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { Observable, Subject, catchError, combineLatest, filter, merge, of } from 'rxjs';
import { constants } from 'src/app/core/constants/constants';
import { PermissionResource } from 'src/app/core/constants/permissions.constants';
import { PermissionService } from 'src/app/core/services/permission.service';
import { DOC_STATUS } from 'src/app/core/models/status.enum';
import { selectDefaultContentLanguageId, selectUser } from 'src/app/core/store/selectors';
import { AppState } from 'src/app/core/store/state';
import { isPresent } from 'src/app/core/utils/isPresent';
import { CounterpartiesActions } from 'src/app/modules/counterparties/store/actions';
import {
  PartiesApiService,
  PartyDto,
} from 'src/app/modules/counterparties/services/parties-api.service';
import { GetDocumentTypeDto } from 'src/app/modules/doc-types/models/doc-types.model';
import { DocumentTypesApiService } from 'src/app/modules/doc-types/services/document-types-api.service';
import { DocTypesActions } from 'src/app/modules/doc-types/store/actions';
import { UserDto } from 'src/app/modules/login/services/user-api.service';
import { PlaybookDto } from 'src/app/modules/playbooks/models/playbooks.model';
import { buildPlaybookPreviewName, PlaybookNameContext } from 'src/app/modules/playbooks/playbooks.helper';
import { PlaybooksApiService } from 'src/app/modules/playbooks/services/playbooks-api.service';
import { AlertType } from 'src/app/shared/components/app-alert/app-alert.component';
import { LanguageDto } from 'src/app/shared/services/languages-api.service';
import { PositionDto, PositionsApiService } from 'src/app/shared/services/positions-api.service';
import { AppButtonSize, AppButtonType } from 'src/app/shared/shared.model';
import { selectContentLanguages } from 'src/app/shared/store/selectors';
import { CreateDocumentDto, DocumentPropertiesDto } from '../../../models/documents.model';
import { DocumentsActions } from '../../../store/actions';
import { fromDocuments } from '../../../store/selectors';

type PlaybookOption = PlaybookDto & {
  displayName: string;
  documentTypeName: string;
  positionName: string;
  languageName: string;
};

@UntilDestroy()
@Component({
  selector: 'app-doc-upload-modal',
  templateUrl: './doc-upload-modal.component.html',
  styleUrl: './doc-upload-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocUploadModalComponent implements OnInit, AfterViewInit {
  public onClose: Subject<boolean> = new Subject<boolean>();
  public form: FormGroup | undefined;
  public addDocTypeForm: FormGroup | undefined;
  public addPartyForm: FormGroup | undefined;
  public body: string | undefined;
  public title: string = this.translocoService.translate(`titles.addDocument`);
  public btnTypes = AppButtonType;
  public btnSizes = AppButtonSize;
  public alertTypes = AlertType;
  public constants = constants;

  public files: NgxFileDropEntry[] = [];
  public showFileUploadError: boolean = false;
  public fileUploadErrorMessage: string = '';

  public docTypeItems: GetDocumentTypeDto[] = [];
  public positionItems: PositionDto[] = [];
  public conterPartyItems: PartyDto[] = [];
  public hiddenConterPartyItems: number[] = [];
  public playbookItems: PlaybookOption[] = [];
  public selectedPlaybookId: string | null = null;
  public showAllPlaybooks: boolean = false;
  public readonly suggestedPlaybooksPreviewCount: number = 3;

  public creatingNewDocType: boolean = false;
  public creatingNewParty: boolean = false;

  public user$: Observable<UserDto | null> | undefined;
  public currentUser: UserDto | null | undefined;
  public isDocumentCreating$ = this.store$.select(fromDocuments.selectDocumentCreating);
  public isSelectedDocumentFileUploading: boolean = false;
  public contentLanguages: LanguageDto[] = [];
  public canWrite = false;
  private canWriteDocumentTypes = false;
  private canWriteCounterparties = false;
  private canWriteFileUpload = false;

  private selectedDocumentFileId: string | null = null;

  constructor(
    private store$: Store<AppState>,
    private bsModalRef: BsModalRef,
    private elementRef: ElementRef,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly documentTypesApiService: DocumentTypesApiService,
    private readonly positionsApiService: PositionsApiService,
    private readonly partiesApiService: PartiesApiService,
    private readonly playbooksApiService: PlaybooksApiService,
    private readonly translocoService: TranslocoService,
    private readonly router: Router,
    private readonly permissionService: PermissionService,
  ) {}

  ngAfterViewInit(): void {
    this.store$
      .select(fromDocuments.selectCloseCreatingDocumentModal)
      .pipe(untilDestroyed(this))
      .subscribe((close) => {
        if (close) {
          this.bsModalRef.hide();
          this.store$.dispatch(DocumentsActions.clearCreateDocumentCancelled());
        }
      });
  }

  public ngOnInit(): void {
    this.initSelectedDocumentFileUploadState();
    this.initForm();
    combineLatest([
      this.permissionService.canWrite$(PermissionResource.Documents),
      this.permissionService.canWrite$(PermissionResource.DocumentTypes),
      this.permissionService.canWrite$(PermissionResource.Counterparties),
      this.permissionService.canWrite$(PermissionResource.FileUpload),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([canWrite, canWriteDocumentTypes, canWriteCounterparties, canWriteFileUpload]) => {
        this.canWrite = canWrite;
        this.canWriteDocumentTypes = canWriteDocumentTypes;
        this.canWriteCounterparties = canWriteCounterparties;
        this.canWriteFileUpload = canWriteFileUpload;
        this.syncFormAccess();
      });
  }

  initForm() {
    combineLatest([
      this.store$.select(selectUser).pipe(filter(isPresent)),
      this.store$.select(selectDefaultContentLanguageId),
      this.store$.select(selectContentLanguages).pipe(filter(isPresent)),
      this.documentTypesApiService.getDocumentTypes$(),
      this.positionsApiService.getPositions$(),
      this.partiesApiService.getParties$(),
      this.playbooksApiService.getPlaybooks$().pipe(catchError(() => of([] as PlaybookDto[]))),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([
          user,
          defaultContentLanguageId,
          contentLanguages,
          docTypes,
          postitions,
          parties,
          playbooks,
        ]) => {
          this.currentUser = user;
          this.contentLanguages = contentLanguages;
          this.docTypeItems = docTypes;
          this.positionItems = postitions;
          this.conterPartyItems = parties;
          this.hiddenConterPartyItems = [];
          this.playbookItems = this.buildPlaybookOptions(playbooks);

          if (this.selectedPlaybookId) {
            const hasSelectedPlaybook = this.playbookItems.some(
              (playbook) => playbook.uniqueId === this.selectedPlaybookId,
            );

            if (!hasSelectedPlaybook) {
              this.selectedPlaybookId = null;
            }
          }

          if (!this.form) {
            this.form = this.fb.group({
              documentName: ['', { validators: [Validators.required] }],
              documentType: ['', { validators: [Validators.required] }],
              counterparties: ['', { validators: [Validators.required] }],
              myPosition: ['', { validators: [Validators.required] }],
              languageId: [defaultContentLanguageId, { validators: [Validators.required] }],
              playbookEnabled: [true],
              playbookSearch: [''],
            });

            this.initPlaybookStateListeners();
          } else if (!this.form.get('languageId')?.value && defaultContentLanguageId) {
            this.form.get('languageId')?.patchValue(defaultContentLanguageId, { emitEvent: false });
          }

          if (!this.addDocTypeForm) {
            this.addDocTypeForm = this.fb.group({
              newDocumentType: ['', { validators: [Validators.required] }],
            });
          }

          if (!this.addPartyForm) {
            this.addPartyForm = this.fb.group({
              newPartyName: ['', { validators: [Validators.required] }],
            });
          }

          this.syncFormAccess();
          this.cdr.markForCheck();
        },
      );
  }

  openNewDocTypeForm() {
    if (!this.canCreateNewDocType) {
      return;
    }

    this.creatingNewDocType = true;
    this.cdr.markForCheck();
  }

  onConfirmNewDocType() {
    if (!this.canCreateNewDocType) {
      return;
    }

    this.documentTypesApiService
      .createDocumentType$(this.addDocTypeForm?.get('newDocumentType')?.value)
      .pipe(
        untilDestroyed(this),
        catchError(() => {
          this.addDocTypeForm?.get('newDocumentType')?.setErrors({ docTypeNameAlreadyExist: true });
          const invalidControl = this.elementRef.nativeElement.querySelector('#newDocumentType');
          invalidControl?.focus();
          return of();
        }),
      )
      .subscribe((newDocType: GetDocumentTypeDto) => {
        if (newDocType) {
          this.documentTypesApiService
            .getDocumentTypes$()
            .pipe(untilDestroyed(this))
            .subscribe((docTypeItems) => {
              this.store$.dispatch(DocTypesActions.setDocTypes({ docTypes: docTypeItems }));
              this.docTypeItems = docTypeItems;
              this.cdr.detectChanges();

              setTimeout(() => {
                this.form?.get('documentType')?.patchValue(newDocType.id);
                this.form?.updateValueAndValidity();
                this.onCancelNewDocType();
                this.cdr.detectChanges();
              });
            });
        } else {
          this.onCancelNewDocType();
        }
      });
  }

  get canCreateNewDocType(): boolean {
    return this.canWrite && this.canWriteDocumentTypes;
  }

  onCancelNewDocType() {
    this.addDocTypeForm?.reset();
    this.creatingNewDocType = false;
    this.cdr.markForCheck();
  }

  openNewPartyForm() {
    if (!this.canCreateNewCounterparty) {
      return;
    }

    this.creatingNewParty = true;
    this.cdr.markForCheck();
  }

  onConfirmNewParty() {
    if (!this.canCreateNewCounterparty) {
      return;
    }

    this.partiesApiService
      .createParty$(this.addPartyForm?.get('newPartyName')?.value)
      .pipe(
        untilDestroyed(this),
        catchError(() => {
          this.addPartyForm?.get('newPartyName')?.setErrors({ partyNameAlreadyExist: true });
          const invalidControl = this.elementRef.nativeElement.querySelector('#newPartyName');
          invalidControl?.focus();
          return of();
        }),
      )
      .subscribe((resp: PartyDto) => {
        if (resp) {
          this.store$.dispatch(CounterpartiesActions.getCounterparties());

          this.partiesApiService
            .getParties$()
            .pipe(untilDestroyed(this))
            .subscribe((parties) => {
              this.conterPartyItems = parties;
              this.cdr.detectChanges();

              setTimeout(() => {
                this.form?.get('counterparties')?.patchValue([resp.id]);
                this.form?.updateValueAndValidity();
                this.onCancelNewParty();
                this.creatingNewParty = false;
                this.cdr.detectChanges();
              });
            });
        } else {
          this.creatingNewParty = false;
        }
      });
  }

  onCancelNewParty() {
    this.addPartyForm?.reset();
    this.creatingNewParty = false;
    this.cdr.markForCheck();
  }

  get canCreateNewCounterparty(): boolean {
    return this.canWrite && this.canWriteCounterparties;
  }

  public openCreatePlaybookPage(): void {
    if (!this.canWrite) {
      return;
    }

    this.bsModalRef.hide();
    this.router.navigate(['/playbooks', 'new']);
  }

  public revealAllPlaybooks(): void {
    if (!this.canWrite) {
      return;
    }

    this.showAllPlaybooks = true;
    this.cdr.markForCheck();
  }

  public collapseToSuggestedPlaybooks(): void {
    if (!this.canWrite) {
      return;
    }

    this.showAllPlaybooks = false;
    this.cdr.markForCheck();
  }

  public togglePlaybookSelection(playbookId: string): void {
    if (!this.canWrite) {
      return;
    }

    this.selectedPlaybookId = this.selectedPlaybookId === playbookId ? null : playbookId;
    this.cdr.markForCheck();
  }

  public dropped(files: NgxFileDropEntry[]) {
    if (!this.canUploadFiles) {
      return;
    }

    if (files.length > 1) {
      return;
    }

    const droppedFile = files[0];

    if (droppedFile.fileEntry.isFile) {
      const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
      fileEntry.file((file: File) => {
        if (constants.fileUploaderAcceptedFormatsTypes.indexOf(file.type) !== -1) {
          const requestKey = this.getFileRequestKey(file);

          this.showFileUploadError = false;
          this.fileUploadErrorMessage = '';
          this.files = files;
          this.showAllPlaybooks = false;
          const fileNameWithoutExt = files[0].fileEntry.name.split('.').slice(0, -1).join('.');
          this.form?.get('documentName')?.patchValue(fileNameWithoutExt);
          this.store$.dispatch(DocumentsActions.clearSelectedDocumentFileUpload());
          this.store$.dispatch(DocumentsActions.uploadSelectedDocumentFile({ file, requestKey }));
          this.cdr.markForCheck();
        } else {
          this.showFileUploadError = true;
          this.fileUploadErrorMessage = this.translocoService.translate('errors.wrongFileTypeDocxPdf');
          this.store$.dispatch(DocumentsActions.clearSelectedDocumentFileUpload());
          this.cdr.markForCheck();
        }
      });
    }
  }

  public get isFileSelected(): boolean {
    return this.files?.length > 0;
  }

  public get isSubmitDisabled(): boolean {
    return (
      !this.form ||
      !this.isFileSelected ||
      this.form.invalid ||
      this.isSelectedDocumentFileUploading ||
      !this.canUploadFiles ||
      !this.selectedDocumentFileId
    );
  }

  public get isPlaybookEnabled(): boolean {
    return this.form?.get('playbookEnabled')?.value !== false;
  }

  public get hasAvailablePlaybooks(): boolean {
    return this.playbookItems.length > 0;
  }

  public get showPlaybookOptionalInfo(): boolean {
    return !this.isPlaybookEnabled;
  }

  public get hasSuggestedPlaybookCriteria(): boolean {
    return !!(
      this.getNumericValue(this.form?.get('documentType')?.value) &&
      this.getNumericValue(this.form?.get('languageId')?.value) &&
      this.getNumericValue(this.form?.get('myPosition')?.value)
    );
  }

  public get visibleSuggestedPlaybooks(): PlaybookOption[] {
    if (!this.showSuggestedPlaybooksSection) {
      return [];
    }

    return this.filteredSuggestedPlaybooks;
  }

  public get visibleAllPlaybooks(): PlaybookOption[] {
    if (!this.showAllPlaybooksSection) {
      return [];
    }

    if (
      this.playbookSearchTerm ||
      this.showAllPlaybooks ||
      this.showSuggestedPlaybooksEmptyState ||
      this.hasSuggestedPlaybookCriteria
    ) {
      return this.filteredAlternativePlaybooks;
    }

    return this.filteredAlternativePlaybooks.slice(0, this.suggestedPlaybooksPreviewCount);
  }

  public get showSuggestedPlaybooksSection(): boolean {
    if (!this.hasSuggestedPlaybookCriteria) {
      return false;
    }

    if (this.playbookSearchTerm) {
      return this.filteredSuggestedPlaybooks.length > 0;
    }

    return this.filteredSuggestedPlaybooks.length > 0 || this.showSuggestedPlaybooksEmptyState;
  }

  public get showSuggestedPlaybooksEmptyState(): boolean {
    return this.hasSuggestedPlaybookCriteria && !this.playbookSearchTerm && !this.suggestedPlaybooks.length;
  }

  public get showAllPlaybooksSection(): boolean {
    if (!this.hasAvailablePlaybooks) {
      return false;
    }

    if (this.playbookSearchTerm) {
      return this.filteredAlternativePlaybooks.length > 0;
    }

    if (this.showSuggestedPlaybooksEmptyState) {
      return this.filteredAlternativePlaybooks.length > 0;
    }

    if (this.hasSuggestedPlaybookCriteria && this.suggestedPlaybooks.length && !this.showAllPlaybooks) {
      return false;
    }

    return this.filteredAlternativePlaybooks.length > 0;
  }

  public get showExpandAllPlaybooksLink(): boolean {
    if (this.showAllPlaybooks || this.playbookSearchTerm || !this.hasAvailablePlaybooks) {
      return false;
    }

    if (this.hasSuggestedPlaybookCriteria) {
      return this.suggestedPlaybooks.length > 0 && this.filteredAlternativePlaybooks.length > 0;
    }

    return this.filteredAlternativePlaybooks.length > this.visibleAllPlaybooks.length;
  }

  public get showCollapseSuggestedPlaybooksLink(): boolean {
    return (
      this.hasSuggestedPlaybookCriteria &&
      this.suggestedPlaybooks.length > 0 &&
      this.showAllPlaybooks &&
      !this.playbookSearchTerm
    );
  }

  public get showNoFilteredPlaybooksState(): boolean {
    return !!this.playbookSearchTerm && !this.filteredPlaybooks.length;
  }

  public isPlaybookSelected(playbookId: string): boolean {
    return this.selectedPlaybookId === playbookId;
  }

  public onConfirm(): void {
    if (!this.canUploadFiles || !this.files.length || !this.form || !this.selectedDocumentFileId) {
      return;
    }

    const properties = {
      name: this.form?.get('documentName')?.value,
      type: this.getNumericValue(this.form?.get('documentType')?.value) || 0,
      status: DOC_STATUS.INPROGRESS,
      userPositionId: this.getNumericValue(this.form?.get('myPosition')?.value) || 0,
      parties: this.getSelectedIds(this.form?.get('counterparties')?.value),
      languageId: this.getNumericValue(this.form?.get('languageId')?.value) || 0,
      revisionOption: this.currentUser?.defaultRevisionOption,
    } as DocumentPropertiesDto;

    const model = {
      fileId: this.selectedDocumentFileId,
      assignedPlaybookId: this.selectedPlaybookId || undefined,
      properties,
    } as CreateDocumentDto;

    this.store$.dispatch(DocumentsActions.createDocument({ model }));
    this.bsModalRef.hide();
  }

  public onCancel(): void {
    const pristine = !!this.form?.pristine && this.files?.length === 0;
    this.store$.dispatch(DocumentsActions.createDocumentCancel({ pristine }));
  }

  private initSelectedDocumentFileUploadState(): void {
    combineLatest([
      this.store$.select(fromDocuments.selectSelectedDocumentFileId),
      this.store$.select(fromDocuments.selectSelectedDocumentFileUploadError),
      this.store$.select(fromDocuments.selectSelectedDocumentFileUploading),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([selectedDocumentFileId, fileUploadErrorMessage, isSelectedDocumentFileUploading]) => {
        this.selectedDocumentFileId = selectedDocumentFileId;
        this.fileUploadErrorMessage = fileUploadErrorMessage || '';
        this.showFileUploadError = !!fileUploadErrorMessage;
        this.isSelectedDocumentFileUploading = isSelectedDocumentFileUploading;
        this.cdr.markForCheck();
      });
  }

  private get suggestedPlaybooks(): PlaybookOption[] {
    if (!this.hasSuggestedPlaybookCriteria) {
      return [];
    }

    const documentTypeId = this.getNumericValue(this.form?.get('documentType')?.value);
    const languageId = this.getNumericValue(this.form?.get('languageId')?.value);
    const positionId = this.getNumericValue(this.form?.get('myPosition')?.value);

    return this.playbookItems.filter(
      (playbook) =>
        playbook.documentTypeId === documentTypeId &&
        playbook.languageId === languageId &&
        playbook.positionId === positionId,
    );
  }

  private get playbookSearchTerm(): string {
    return `${this.form?.get('playbookSearch')?.value || ''}`.trim().toLowerCase();
  }

  private get filteredPlaybooks(): PlaybookOption[] {
    if (!this.playbookSearchTerm) {
      return this.playbookItems;
    }

    return this.playbookItems.filter((playbook) =>
      this.matchesPlaybookSearch(playbook, this.playbookSearchTerm),
    );
  }

  private get filteredSuggestedPlaybooks(): PlaybookOption[] {
    if (!this.hasSuggestedPlaybookCriteria) {
      return [];
    }

    const suggestedIds = new Set(this.suggestedPlaybooks.map((playbook) => playbook.uniqueId));

    return this.filteredPlaybooks.filter((playbook) => suggestedIds.has(playbook.uniqueId));
  }

  private get filteredAlternativePlaybooks(): PlaybookOption[] {
    if (!this.hasSuggestedPlaybookCriteria) {
      return this.filteredPlaybooks;
    }

    const suggestedIds = new Set(this.suggestedPlaybooks.map((playbook) => playbook.uniqueId));

    return this.filteredPlaybooks.filter((playbook) => !suggestedIds.has(playbook.uniqueId));
  }

  private buildPlaybookOptions(playbooks: PlaybookDto[]): PlaybookOption[] {
    const context = this.getPlaybookNameContext();

    return [...playbooks]
      .map((playbook) => {
        const documentTypeName =
          context.docTypeItems.find((docType) => docType.id === playbook.documentTypeId)?.name ||
          context.selectPlaceholder;
        const positionName =
          context.positionItems.find((position) => position.id === playbook.positionId)?.name ||
          context.selectPlaceholder;
        const languageName =
          context.contentLanguages.find((language) => language.id === playbook.languageId)
            ?.displayName || context.selectPlaceholder;

        return {
          ...playbook,
          displayName: buildPlaybookPreviewName(playbook, context),
          documentTypeName,
          positionName,
          languageName,
        };
      })
      .sort((left, right) => left.displayName.localeCompare(right.displayName));
  }

  private getPlaybookNameContext(): PlaybookNameContext {
    return {
      docTypeItems: this.docTypeItems || [],
      positionItems: this.positionItems || [],
      contentLanguages: this.contentLanguages || [],
      selectPlaceholder: this.translocoService.translate('placeholders.select'),
      nonePlaceholder: this.translocoService.translate('text.none'),
    };
  }

  private matchesPlaybookSearch(playbook: PlaybookOption, searchTerm: string): boolean {
    const searchTokens = searchTerm.split(/\s+/).filter(Boolean);

    if (!searchTokens.length) {
      return true;
    }

    const searchableText = [
      playbook.displayName,
      playbook.name,
      playbook.documentTypeName,
      playbook.positionName,
      playbook.languageName,
    ]
      .join(' ')
      .toLowerCase();

    return searchTokens.every((token) => searchableText.includes(token));
  }

  private initPlaybookStateListeners(): void {
    merge(
      this.form?.get('documentType')?.valueChanges || of(null),
      this.form?.get('languageId')?.valueChanges || of(null),
      this.form?.get('myPosition')?.valueChanges || of(null),
    )
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.showAllPlaybooks = false;
        this.cdr.markForCheck();
      });
  }

  private getSelectedIds(value: unknown): number[] {
    if (Array.isArray(value)) {
      return value
        .map((item) => this.getNumericValue(item))
        .filter((item): item is number => item != null);
    }

    if (typeof value === 'string') {
      return value
        .split(',')
        .map((item) => this.getNumericValue(item.trim()))
        .filter((item): item is number => item != null);
    }

    const numericValue = this.getNumericValue(value);
    return numericValue != null ? [numericValue] : [];
  }

  private getNumericValue(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : undefined;
  }

  private getFileRequestKey(file: File): string {
    return `${file.name}::${file.size}::${Date.now()}`;
  }

  public get canUploadFiles(): boolean {
    return this.canWrite && this.canWriteFileUpload;
  }

  private syncFormAccess(): void {
    const forms = [this.form, this.addDocTypeForm, this.addPartyForm].filter(
      (form): form is FormGroup => !!form,
    );

    if (this.canWrite) {
      forms.forEach((form) => form.enable({ emitEvent: false }));
      return;
    }

    this.creatingNewDocType = false;
    this.creatingNewParty = false;
    forms.forEach((form) => form.disable({ emitEvent: false }));
  }
}
